import { Box, Button, Container, Typography } from "@mui/material";
import styled from "@mui/material/styles/styled";
import { PrimaryActionButton } from "../shared/buttons.styles";

export const StyledContainer = styled(Container, {
  shouldForwardProp: (prop) => prop !== "$showTemplates",
})<{ $showTemplates?: boolean }>(({ theme, $showTemplates }) => ({
  paddingTop: theme.spacing(1),
  paddingBottom: $showTemplates ? theme.spacing(0) : theme.spacing(10),
  paddingLeft: 0,
  paddingRight: 0,
  maxWidth: "1500px",
  margin: "0 auto",
  [theme.breakpoints.up("md")]: {
    paddingTop: theme.spacing(3),
    paddingBottom: $showTemplates ? theme.spacing(0) : theme.spacing(6),
  },
}));

export const HeroSection = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$showTemplates",
})<{ $showTemplates?: boolean }>(({ theme, $showTemplates }) => ({
  textAlign: "center",
  paddingTop: theme.spacing(4),
  marginBottom: $showTemplates ? theme.spacing(0) : theme.spacing(3),
  [theme.breakpoints.up("md")]: {
    marginBottom: $showTemplates ? theme.spacing(0) : theme.spacing(4),
  },
}));

export const HeroTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 400,
  fontSize: 26,
  lineHeight: 1.35,
  textAlign: "center",
  width: "95%",
  margin: "0 auto",
  marginBottom: theme.spacing(1),
  [theme.breakpoints.up("md")]: {
    fontSize: 42,
    lineHeight: 1.28,
    width: "100%",
    fontWeight: 500,
  },
  "& .highlight": {
    fontWeight: 550,
    background:
      "linear-gradient(89.95deg, #5B47F5 1.57%, #B782FF 50.91%, #D0B1FE 98.39%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
}));

export const HeroSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  lineHeight: "18px",
  textAlign: "center",
  color: "#6B7280",
  width: "85%",
  margin: "0 auto",
  [theme.breakpoints.up("md")]: {
    fontSize: "18px",
    lineHeight: "24px",
    width: "100%",
  },
}));

export const ConceptPromptBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$isEditImage",
})<{ $isEditImage?: boolean }>(({ theme, $isEditImage }) => ({
  position: "relative",
  maxWidth: "802px",
  width: "100%",
  marginLeft: "auto",
  marginRight: "auto",
  marginBottom: theme.spacing(4),
  boxSizing: "border-box",
  borderRadius: "24px",
  border: "2px solid #E5E7EB",
  boxShadow:
    "0 4px 12px rgba(15, 23, 42, 0.1), 0 16px 40px rgba(15, 23, 42, 0.12)",
  backgroundColor: "#FFFFFF",
  overflow: "visible",
  paddingLeft: theme.spacing(2.5),
  paddingRight: theme.spacing(2.5),
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  [theme.breakpoints.up("md")]: {
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(2.5),
    paddingTop: theme.spacing(2.5),
    paddingBottom: $isEditImage ? theme.spacing(1.25) : theme.spacing(2.5),
  },
}));

export const ColorPaletteSection = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  flexShrink: 0,
  marginTop: 0,
  gap: theme.spacing(1),
}));

export const ImageUploadSection = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  height: "100%",
}));

export const PromptSection = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
}));

export const PromptInputContainer = styled(Box)(({ theme }) => ({
  height: "auto",
  display: "flex",
  flexDirection: "column",
  paddingBottom: theme.spacing(0.5),
}));

export const PromptFormPrimaryActionButton = styled(PrimaryActionButton)(
  ({ theme }) => ({
    height: 38,
    fontSize: 12,
    padding: "8px 8px",
    backgroundColor: "#111111",
    color: "#FFFFFF",
    borderRadius: "999px",
    textTransform: "none",
    fontWeight: 600,
    boxShadow: "none",
    [theme.breakpoints.up("md")]: {
      height: 48,
      fontSize: 16,
      padding: "12px 24px",
    },
    "&:hover": {
      backgroundColor: "#1F2937",
      boxShadow: "none",
    },
    "&:disabled": {
      backgroundColor: "#E5E7EB",
      color: "#9CA3AF",
    },
  }),
);

export const ActionButtonsRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  flexWrap: "wrap",
  marginTop: theme.spacing(2),
  [theme.breakpoints.up("md")]: {
    marginTop: theme.spacing(3),
  },
}));

export const FormModeSwitch = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "flex",
  alignItems: "stretch",
  width: "100%",
  maxWidth: 540,
  boxSizing: "border-box",
  backgroundColor: "#F3F3F3",
  borderRadius: 9999,
  boxShadow:
    "0 2px 8px rgba(15, 23, 42, 0.1), 0 1px 2px rgba(15, 23, 42, 0.06)",
  padding: theme.spacing(1),
  [theme.breakpoints.up("md")]: {
    maxWidth: 612,
  },
}));

const FORM_MODE_SLOT = { generate: 0, editImage: 1, template: 2 } as const;

/** Sliding pill behind a 3-slot FormModeSwitch; `$index` (0–2) overrides `$mode`. */
export const FormModeSlider = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$mode" && prop !== "$index",
})<{ $mode?: keyof typeof FORM_MODE_SLOT; $index?: number }>(
  ({ $mode, $index }) => ({
    position: "absolute",
    left: 8,
    top: 8,
    bottom: 8,
    width: "calc((100% - 16px) / 3)",
    borderRadius: 9999,
    backgroundColor: "#FFFFFF",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
    pointerEvents: "none",
    transition: "transform 0.24s ease",
    transform: `translateX(${($index ?? FORM_MODE_SLOT[$mode ?? "generate"]) * 100}%)`,
  }),
);

/** Round arrow submit button inside the prompt box (Brief + Image Edit upload). */
export const promptFabSx = (isDesktop: boolean) => ({
  borderRadius: "50%",
  minWidth: 0,
  width: isDesktop ? 48 : 44,
  height: isDesktop ? 48 : 44,
  p: 0,
  transform: "translateY(-12px)",
  "&:hover": { transform: "translateY(-12px)" },
  "&:active": { transform: "translateY(-12px)" },
});

export const FormModeSwitchButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>(({ theme, selected }) => ({
  position: "relative",
  zIndex: 1,
  flex: 1,
  minWidth: 0,
  height: 32,
  fontSize: 11,
  padding: "6px 6px",
  borderRadius: 9999,
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "none",
  border: "none",
  whiteSpace: "nowrap",
  backgroundColor: "transparent",
  color: selected ? "#111111" : "#6B7280",
  transition:
    "color 0.28s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.28s ease, background-color 0.2s ease",
  opacity: 1,
  "& .MuiButton-startIcon": {
    marginRight: "3px",
    marginLeft: "0px",
    opacity: selected ? 1 : 0.72,
    transition: "opacity 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
  },
  [theme.breakpoints.up("sm")]: {
    padding: "6px 10px",
    "& .MuiButton-startIcon": {
      marginRight: "4px",
      marginLeft: "-2px",
    },
  },
  [theme.breakpoints.up("md")]: {
    height: 40,
    fontSize: 13,
    padding: "8px 14px",
  },
  "&:hover": {
    backgroundColor: "transparent",
    color: selected ? "#111111" : "#6B7280",
  },
  "&.MuiButton-text:hover": {
    backgroundColor: "transparent",
  },
}));
